import { cn } from '@invent/wl-ui-kit-next';
import { Tooltip, TooltipContent, TooltipTrigger } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { Link } from 'react-router-dom';

import type {
  IAdminSecAdvFirmFeed,
  ISecAdvCatalogue,
  ISecAdvCatalogueAttribute,
  ISecAdvCatalogueElement
} from '../../api/sec-adv-contracts';
import { SEC_ADV_FIRMS_PATH } from '../../components/admin-page';
import { firmSourcePath } from '../../components/firm-link';

/**
 * What the SEC ADV register's list and firm page share (SR-423): the dash an empty value reads as,
 * the link from a Form ADV value to its source in the firm's filing, the amount a filter box
 * accepts, the last list address the way back returns to, and the firms viewed last.
 */

/** The register's key number in its header, the same on both tabs — every firm it holds and its
 *  feed's date, from the status read: "24,054 firms · feed Oct 28". Null while that is unread. */
export function registerSummary(feed: IAdminSecAdvFirmFeed | undefined): Nullable<string> {
  if (!feed) return null;
  const total = feed.firmTypes.reduce((sum, type) => sum + type.firms, 0);
  const day = feed.latest?.generatedOn;
  return `${total.toLocaleString('en-US')} ${total === 1 ? 'firm' : 'firms'}${day ? ` · feed ${shortDay(day)}` : ''}`;
}

function shortDay(day: string): string {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(year, month - 1, date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Why an exempt reporting adviser's amounts are a dash. */
export const ERA_NOTE = 'Exempt reporting adviser – AUM not reported';

/** An empty value — never "Not answered" on the register's screens — with why on hover where
 *  there is a reason, and on focus outside a table: a cell's would make a tab stop per row. */
export function Dash({ title, focusable = true }: { title?: string; focusable?: boolean }) {
  if (!title) return <span className="text-muted-foreground">–</span>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={focusable ? 0 : undefined}
          aria-label={title}
          className="cursor-help text-muted-foreground"
        >
          –
        </span>
      </TooltipTrigger>
      <TooltipContent>{title}</TooltipContent>
    </Tooltip>
  );
}

/** A Form ADV item as the SEC's monthly adviser files head their columns: `5.F(2)(c)` →
 *  `5F(2)(c)`. */
export function secItem(item: string): string {
  return item.replace(/^(\d+)\.([A-Z])/, '$1$2');
}

/** A value taken from Form ADV, as the way to that answer in the firm's latest ADV Part 1. */
export function SourceLink({
  firmCrd,
  source,
  className,
  children
}: {
  firmCrd: string;
  /** `<element path>.<attribute>`, or an element's path. */
  source: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={firmSourcePath(firmCrd, source)}
      className={cn(
        'decoration-[var(--veritant-royal-55)] decoration-dotted underline-offset-4',
        'hover:text-[var(--color-sapphire)] hover:underline',
        className
      )}
    >
      {children}
    </Link>
  );
}

const SCALE: Record<string, number> = { K: 1e3, M: 1e6, B: 1e9, T: 1e12 };

/**
 * An amount typed into a filter — `30B`, `30.3B`, `500M`, `$30,000,000,000` and `30000000000`
 * alike — as whole dollars, or null when it reads as none. A comma separates thousands and nothing
 * else: `1,5B` is no amount.
 */
export function parseAmount(text: string): Nullable<number> {
  const compact = text.replace(/[\s$]/g, '').toUpperCase();
  if (compact === '') return null;
  const match = /^(\d{1,3}(?:,\d{3})+|\d+)(\.\d+)?([KMBT])?$/.exec(compact);
  if (!match) return null;
  const number = Number(`${match[1].replace(/,/g, '')}${match[2] ?? ''}`);
  const amount = Math.round(number * (match[3] ? SCALE[match[3]] : 1));
  return Number.isSafeInteger(amount) ? amount : null;
}

const COMPACT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1
});

/** `$30.3B` — an amount where room is short: a chip, a chart's centre. */
export function compactDollars(amount: number): string {
  return COMPACT.format(amount);
}

const PERCENT = new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 1 });

/** A share, `0.2 %` down to "< 0.1 %". */
export function formatShare(share: number): string {
  if (share > 0 && share < 0.001) return '< 0.1%';
  return PERCENT.format(share);
}

// ---------------------------------------------------------------------------
// The catalogue, read by path
// ---------------------------------------------------------------------------

/** The element at `path` — `FormInfo.Part1A.Item5G`. */
export function elementAt(
  catalogue: Nullable<ISecAdvCatalogue>,
  path: string
): Nullable<ISecAdvCatalogueElement> {
  return catalogue?.elements.find((element) => element.path === path) ?? null;
}

/** The attribute a key names — `FormInfo.Part1A.Item5F.Q5F2C` — with its element. */
export function attributeAt(
  catalogue: Nullable<ISecAdvCatalogue>,
  key: string
): Nullable<{ element: ISecAdvCatalogueElement; attribute: ISecAdvCatalogueAttribute }> {
  const dot = key.lastIndexOf('.');
  if (dot < 0) return null;
  const element = elementAt(catalogue, key.slice(0, dot));
  const attribute = element?.attributes.find((entry) => entry.name === key.slice(dot + 1));
  return element && attribute ? { element, attribute } : null;
}

/** A Part 1A attribute by its name alone — `Q11D2`, `Q5G1` — and where it lives. */
export function part1AKey(catalogue: Nullable<ISecAdvCatalogue>, name: string): Nullable<string> {
  for (const element of catalogue?.elements ?? []) {
    if (!element.path.startsWith('FormInfo.Part1A.')) continue;
    if (element.attributes.some((attribute) => attribute.name === name)) {
      return `${element.path}.${name}`;
    }
  }
  return null;
}

export const ITEM_5G = 'FormInfo.Part1A.Item5G';
export const ITEM_5E = 'FormInfo.Part1A.Item5E';

/** The yes-or-no questions of an item — 5.G's services, 5.E's compensation — as filter options
 *  and pill labels, in the form's order. */
export function yesNoOptions(
  catalogue: Nullable<ISecAdvCatalogue>,
  path: string
): { value: string; label: string }[] {
  return (elementAt(catalogue, path)?.attributes ?? [])
    .filter((attribute) => attribute.valueType === 'yes_no')
    .map((attribute) => ({
      value: attribute.name,
      label: SHORT_OPTIONS[attribute.name] ?? shortLabel(attribute.label)
    }));
}

/** 5.G's services and 5.E's compensation as pills say them — RIA Pulse's words. */
const SHORT_OPTIONS: Record<string, string> = {
  Q5G1: 'Financial planning',
  Q5G2: 'Individual portfolio management',
  Q5G3: 'Investment company portfolio management',
  Q5G4: 'Pooled vehicle portfolio management',
  Q5G5: 'Institutional portfolio management',
  Q5G6: 'Pension consulting',
  Q5G7: 'Adviser / fund manager selection',
  Q5G8: 'Periodicals or newsletters',
  Q5G9: 'Security ratings or pricing',
  Q5G10: 'Market timing',
  Q5G11: 'Educational seminars',
  Q5G12: 'Other services',
  Q5E1: 'Percentage of AUM',
  Q5E2: 'Hourly charges',
  Q5E3: 'Subscription fees',
  Q5E4: 'Fixed fees',
  Q5E5: 'Commissions',
  Q5E6: 'Performance-based fees',
  Q5E7: 'Other compensation'
};

/** A 5.G or 5.E label as a pill says it: "Compensated by hourly charges" → "Hourly charges". */
function shortLabel(label: string): string {
  const short = label.replace(/^Compensated (by|in) /, '');
  return short.charAt(0).toUpperCase() + short.slice(1);
}

/** A code's pill label among `options`, or the code. */
export function optionLabel(options: { value: string; label: string }[], code: string): string {
  return options.find((option) => option.value === code)?.label ?? code;
}

// ---------------------------------------------------------------------------
// The way back to the list, and the firms viewed last
// ---------------------------------------------------------------------------

const LAST_LIST = 'veritant.secAdv.lastList';

/** Kept by the list on every change of its address, for this tab. */
export function rememberListSearch(search: string): void {
  try {
    window.sessionStorage.setItem(LAST_LIST, search);
  } catch {
    // A refused store: the way back is the plain list.
  }
}

/** The list as last left — its search, filters, sort and columns — or the plain list. */
export function lastListPath(): string {
  try {
    const search = window.sessionStorage.getItem(LAST_LIST) ?? '';
    return `${SEC_ADV_FIRMS_PATH}${search.startsWith('?') ? search : ''}`;
  } catch {
    return SEC_ADV_FIRMS_PATH;
  }
}
